import React from 'react';
import { Check, ChevronDown, Columns3, Sparkles } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Label } from '../ui/label';
import { FoldThumb } from './FoldThumb';
import { Switch } from '../ui/switch';
import { ROMAN_FOLDS, FOLD_TO_PRICE_ID, ROMAN_LININGS, FOLD_INFO, LINING_INFO, setLiningHover } from '../../lib/romanFold';
import { formatPrice } from '../../lib/utils';

const LINING_BLURB = LINING_INFO;
const LINING_SWATCH = { unlined: 'bg-amber-100', privacy: 'bg-stone-400', blackout: 'bg-neutral-800', interlining: 'bg-amber-200 bg-[repeating-linear-gradient(45deg,rgba(255,255,255,0.35)_0_3px,transparent_3px_7px)]' };
const priceText = (p) => (p > 0 ? `+${formatPrice(p)}` : 'Included');

/* Lining choice: Unlined / Privacy / Blackout (prices from Admin → Pricing) */
// Phones: press and hold a lining card to preview it on the scene; releasing ends the preview without selecting
const press = { timer: null, held: false };
const pressPreview = (id) => ({
  onTouchStart: () => { press.held = false; clearTimeout(press.timer); press.timer = setTimeout(() => { press.held = true; setLiningHover(id); navigator.vibrate?.(15); }, 350); },
  onTouchMove: () => clearTimeout(press.timer),
  onTouchEnd: (e) => { clearTimeout(press.timer); if (press.held) { e.preventDefault(); setLiningHover(null); } },
  onTouchCancel: () => { clearTimeout(press.timer); setLiningHover(null); },
  onContextMenu: (e) => e.preventDefault(),
});
const LINING_TIPS = { blackout: 'Bedrooms', privacy: 'Living rooms', interlining: 'Luxe drape' };
const LiningPicker = ({ value, onChange, prices, suggest }) => (
  <div className="mt-5 scroll-mt-24" data-testid="lining-picker" id="lining-picker">
    <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3 block">Lining</Label>
    {suggest && (
      <div className="mb-3 flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 animate-in fade-in slide-in-from-top-1 duration-500" data-testid="lining-suggestion">
        <Sparkles className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-sm text-amber-900">
          Lovely — <span className="font-semibold">{suggest}</span> it is. Now pick a lining: bedrooms? Try <span className="font-semibold">Blackout</span> for full darkness. Living spaces? <span className="font-semibold">Privacy</span> keeps light soft while blocking views.
        </p>
      </div>
    )}
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {ROMAN_LININGS.map(([id, label]) => (
        <button key={id} type="button" onClick={() => { setLiningHover(null); onChange(id); }} data-testid={`lining-${id}`}
          onMouseEnter={() => setLiningHover(id)} onMouseLeave={() => setLiningHover(null)} onFocus={() => setLiningHover(id)} onBlur={() => setLiningHover(null)}
          {...pressPreview(id)}
          className={`relative select-none [-webkit-touch-callout:none] border rounded-lg p-2 text-left transition-colors ${value === id ? 'border-neutral-900 ring-2 ring-neutral-900' : 'border-neutral-200 hover:border-neutral-300'}`}>
          {suggest && LINING_TIPS[id] && <span className="absolute -top-2 right-1.5 rounded-full bg-amber-600 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white" data-testid={`lining-${id}-tip`}>{LINING_TIPS[id]}</span>}
          <span className={`block h-6 rounded mb-1.5 border border-neutral-200 ${LINING_SWATCH[id]}`} />
          <p className="font-medium text-neutral-900 text-sm">{label}</p>
          <p className="text-[11px] text-neutral-500 leading-snug">{LINING_BLURB[id]}</p>
          <p className="text-[11px] font-medium text-amber-700 mt-1" data-testid={`lining-${id}-price`}>{priceText(prices.find(p => p.id === id)?.price || 0)}</p>
        </button>
      ))}
    </div>
  </div>
);

const BLURB = FOLD_INFO;

/* Mini roman shade drawing per fold style */
const FoldIcon = ({ fold }) => (
  <svg viewBox="0 0 60 64" className="h-16 w-auto" aria-hidden="true">
    <rect x="4" y="2" width="52" height="5" rx="1" fill="#404040" />
    {fold === 'cascade' ? [0, 1, 2, 3, 4, 5].map(i => (
      <g key={i}>
        <rect x="6" y={8 + i * 8} width="48" height="8" fill="#ddd0b8" />
        <rect x="6" y={13 + i * 8} width="48" height="3" fill="#a8977a" />
        <line x1="6" x2="54" y1={16 + i * 8} y2={16 + i * 8} stroke="#5f513a" strokeWidth="0.9" />
      </g>
    )) : fold === 'aventura' ? (
      <>
        <rect x="6" y="8" width="48" height="46" fill="#d6c7ad" stroke="#a8977a" strokeWidth="0.8" />
        {[0, 1, 2, 3, 4].map(i => <rect key={i} x="5" y={14 + i * 9} width="50" height="2" rx="1" fill="#6b5a40" />)}
      </>
    ) : fold === 'pleated' ? [0, 1, 2, 3, 4, 5, 6].map(i => (
      <path key={i} d={`M6 ${8 + i * 7} h48 v7 q-24 5 -48 0 z`} fill={i % 2 ? '#cdbd9f' : '#d6c7ad'} stroke="#8f7f63" strokeWidth="0.8" />
    )) : fold === 'hobbled' ? [0, 1, 2, 3, 4].map(i => (
      <path key={i} d={`M6 ${8 + i * 11} h48 v8 q-24 6 -48 0 z`} fill="#d6c7ad" stroke="#a8977a" strokeWidth="0.8" />
    )) : fold === 'relaxed' ? (
      <path d="M6 8 h48 v44 q-24 8 -48 0 z" fill="#d6c7ad" stroke="#a8977a" strokeWidth="0.8" />
    ) : (
      <>
        <rect x="6" y="8" width="48" height="36" fill="#d6c7ad" stroke="#a8977a" strokeWidth="0.8" />
        {[0, 1, 2].map(i => <rect key={i} x="6" y={44 + i * 5} width="48" height="5" fill={i % 2 ? '#c9b898' : '#ddd0b8'} stroke="#a8977a" strokeWidth="0.6" />)}
      </>
    )}
  </svg>
);

export const FoldStyleStep = ({ number, open, onToggle, complete, summary, value, onChange, prices = [], lining, onLiningChange, liningPrices = [], onCompare, footer, suggest, liningSuggest, thumbs, adminThumbs }) => (
  <Collapsible open={open} onOpenChange={onToggle}>
    <CollapsibleTrigger className="w-full" data-testid="step-8-trigger">
      <div className={`flex items-center justify-between p-4 rounded-lg border transition-all ${open ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'}`}>
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${complete ? 'bg-amber-600 text-white' : 'bg-neutral-200 text-neutral-500'}`}>
            {complete ? <Check className="w-4 h-4" /> : number}
          </div>
          <div className="text-left">
            <p className="font-medium text-neutral-900">Fold Style</p>
            <p className="text-sm text-neutral-500" data-testid="fold-style-summary">{summary}</p>
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </div>
    </CollapsibleTrigger>
    <CollapsibleContent>
      <div className="pt-4 pb-2 px-2" data-testid="step-8-content">
        {suggest && (
          <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 animate-in fade-in slide-in-from-top-1 duration-500" data-testid="fold-style-suggestion">
            <Sparkles className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-900">
              Great pick — <span className="font-semibold">{suggest}</span>. Now choose how it folds. <span className="font-semibold">Flat</span> is our most popular, clean and modern look.
            </p>
          </div>
        )}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Select Roman Fold</Label>
            {adminThumbs && (
              <label className="flex items-center gap-1.5 rounded-full border border-dashed border-amber-400 bg-amber-50/60 px-2 py-0.5 text-[10px] font-medium text-amber-800 cursor-pointer" title="Admin only — applies to every customer" data-testid="admin-fold-thumbs-toggle">
                <Switch checked={adminThumbs.enabled} onCheckedChange={adminThumbs.onToggle} className="scale-75 -my-1" data-testid="admin-fold-thumbs-switch" />
                Admin: scene thumbnails {adminThumbs.enabled ? 'on' : 'off'}
              </label>
            )}
          </div>
          <button type="button" onClick={onCompare} className="flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-800 underline-offset-2 hover:underline" data-testid="fold-compare-link">
            <Columns3 className="w-3.5 h-3.5" /> Compare folds
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {ROMAN_FOLDS.map(([id, label]) => {
            const priceId = FOLD_TO_PRICE_ID[id];
            const price = prices.find(p => p.id === priceId)?.price || 0;
            const active = value === priceId;
            return (
              <button key={id} type="button" onClick={() => onChange(id)} data-testid={`fold-style-${id}`}
                className={`border rounded-lg overflow-hidden text-left transition-colors ${active ? 'border-neutral-900 ring-2 ring-neutral-900' : 'border-neutral-200 hover:border-neutral-300'}`}>
                <div className={`relative bg-white flex justify-center ${thumbs?.[id] ? 'h-24 overflow-hidden' : 'py-2'}`}>
                  {thumbs?.[id]
                    ? <FoldThumb src={thumbs[id]} label={label} />
                    : <FoldIcon fold={id} />}
                  {id === 'flat' && <span className="absolute top-1.5 left-1.5 rounded-full bg-amber-600 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white shadow-sm" data-testid="fold-style-flat-popular">Popular</span>}
                </div>
                <div className="p-2 bg-neutral-50">
                  <p className="font-medium text-neutral-900 text-sm">{label}</p>
                  <p className="text-[11px] text-neutral-500 leading-snug">{BLURB[id]}</p>
                  <p className="text-[11px] font-medium text-amber-700 mt-1" data-testid={`fold-style-${id}-price`}>{priceText(price)}</p>
                </div>
              </button>
            );
          })}
        </div>
        <LiningPicker value={lining} onChange={onLiningChange} prices={liningPrices} suggest={liningSuggest} />
      </div>
      {footer}
    </CollapsibleContent>
  </Collapsible>
);
